import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "@/components/base/Modal";
import { supabase } from "@/lib/supabase";
import * as inventoryApi from "@/lib/api/inventory";
import * as productApi from "@/lib/api/products";
import { money, variantLabel } from "@/lib/format";
import { pickDefaultStockLocation, wouldGoNegative } from "@/lib/inventory";
import {
  describeStockPriceSaveOutcome,
  priceInputsDirty,
  stockInputsDirty,
} from "@/lib/stock-price-drawer";
import {
  bulkAdjustSelected,
  bulkClearOverrides,
  bulkSetOverrideAmount,
  buildVariantPriceSaveLines,
  previewEffectivePrice,
  seedOverrideInputs,
  uniformOverridePromotionCandidate,
  type VariantPriceMatrixRow,
} from "@/lib/variant-price-matrix";

type StockRow = VariantPriceMatrixRow & {
  onHand: number;
  available: number;
  determined: boolean;
};

export function QuickStockModal(props: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  productId: string;
  productName: string;
  locationId: string | null;
  locationName: string | null;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<"set" | "add" | "sub">("set");
  const [qty, setQty] = useState("");
  const [rows, setRows] = useState<Record<string, string>>({});
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkAmount, setBulkAmount] = useState("");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [locationId, setLocationId] = useState<string | null>(props.locationId);
  const [locationPicked, setLocationPicked] = useState(false);
  const [forceExceptions, setForceExceptions] = useState(false);

  const { data: locations } = useQuery({
    queryKey: ["quick-stock-locations", props.orgId, props.productId, props.open],
    enabled: props.open && !!props.orgId && !!props.productId,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const [locs, bals] = await Promise.all([
        supabase.from("locations").select("id, name").eq("org_id", props.orgId).order("name"),
        supabase.from("inventory_balances").select("location_id, on_hand").eq("org_id", props.orgId).eq("product_id", props.productId),
      ]);
      if (locs.error) throw new Error(locs.error.message);
      if (bals.error) throw new Error(bals.error.message);
      const byLoc: Record<string, number> = {};
      for (const b of (bals.data ?? []) as { location_id: string; on_hand: number }[]) {
        byLoc[b.location_id] = (byLoc[b.location_id] ?? 0) + Number(b.on_hand ?? 0);
      }
      return ((locs.data ?? []) as { id: string; name: string }[]).map((l) => ({ ...l, onHand: byLoc[l.id] ?? 0 }));
    },
  });

  useEffect(() => {
    if (!props.open) {
      setLocationPicked(false);
      setLocationId(props.locationId);
      return;
    }
    if (locationPicked || !locations || locations.length === 0) return;
    setLocationId(pickDefaultStockLocation(locations, props.locationId));
    setLocationPicked(true);
  }, [props.open, props.locationId, locations, locationPicked]);

  const { data: loaded, error: loadError, refetch, isFetching } = useQuery({
    queryKey: ["quick-stock", props.orgId, props.productId, locationId, props.open],
    enabled: props.open && !!props.orgId && !!props.productId && !!locationId,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async (): Promise<{ productSellingPrice: number | null; variants: StockRow[] }> => {
      const [productRes, variantsRes, balancesRes] = await Promise.all([
        supabase.from("products").select("selling_price").eq("org_id", props.orgId).eq("id", props.productId).maybeSingle(),
        supabase.from("variants").select("id, sku, option_values, status, selling_price").eq("org_id", props.orgId).eq("product_id", props.productId),
        supabase
          .from("inventory_balances")
          .select("variant_id, on_hand, available")
          .eq("org_id", props.orgId)
          .eq("product_id", props.productId)
          .eq("location_id", locationId!),
      ]);
      if (productRes.error) throw new Error(productRes.error.message);
      if (variantsRes.error) throw new Error(variantsRes.error.message);
      if (balancesRes.error) throw new Error(balancesRes.error.message);
      const onHand: Record<string, { onHand: number; available: number }> = {};
      for (const b of (balancesRes.data ?? []) as { variant_id: string | null; on_hand: number; available: number }[]) {
        const key = b.variant_id ?? "product";
        onHand[key] = { onHand: Number(b.on_hand ?? 0), available: Number(b.available ?? 0) };
      }
      const vars = ((variantsRes.data ?? []) as {
        id: string;
        sku: string | null;
        option_values: Record<string, string> | null;
        status: string;
        selling_price: number | null;
      }[])
        .filter((v) => v.status !== "archived")
        .map((v) => {
          const bal = onHand[v.id];
          return {
            id: v.id,
            sku: v.sku,
            label: variantLabel(v.option_values) || "—",
            storedSellingPrice: v.selling_price,
            onHand: bal?.onHand ?? 0,
            available: bal?.available ?? 0,
            determined: true,
          };
        });
      const raw = productRes.data?.selling_price;
      return {
        productSellingPrice: raw == null ? null : Number(raw),
        variants: vars,
      };
    },
  });

  useEffect(() => {
    if (!props.open || !loaded) return;
    setPriceInputs(seedOverrideInputs(loaded.variants));
    setSelected({});
    setBulkAmount("");
    setAdjustAmount("");
    setRows({});
    setQty("");
    setError("");
    setForceExceptions(false);
  }, [props.open, loaded]);

  const variants = loaded?.variants ?? [];
  const productSellingPrice = loaded?.productSellingPrice ?? null;
  const seededPrices = seedOverrideInputs(variants);
  const isVariant = variants.length > 1;
  const simple = variants[0] ?? null;
  const selectedIds = variants.filter((r) => selected[r.id]).map((r) => r.id);
  const builtPreview = buildVariantPriceSaveLines(variants, priceInputs, productSellingPrice);
  const promote = isVariant && builtPreview.ok ? uniformOverridePromotionCandidate(builtPreview.lines, productSellingPrice) : null;
  const allSelected = variants.length > 0 && selectedIds.length === variants.length;
  const targetIds = () => (selectedIds.length ? selectedIds : variants.map((r) => r.id));

  const applyOne = async (variantId: string, n: number, currentOnHand: number) => {
    if (!locationId) throw new Error("Choose a location before adjusting stock.");
    if (mode === "set") {
      await inventoryApi.applyInventorySetQuantity({
        productId: props.productId,
        locationId,
        qty: n,
        reason: "Quick stock edit",
        variantId,
        idempotencyKey: crypto.randomUUID(),
      });
      return;
    }
    const delta = mode === "add" ? n : -n;
    if (delta === 0) return;
    if (wouldGoNegative(currentOnHand, delta)) {
      throw new Error(`Adjustment would drive on-hand below zero (currently ${currentOnHand} on hand)`);
    }
    await inventoryApi.applyInventoryAdjustment({
      productId: props.productId,
      locationId,
      qtyChange: delta,
      reason: "Quick stock edit",
      variantId,
      idempotencyKey: crypto.randomUUID(),
    });
  };

  const saveStock = async () => {
    const fresh = await refetch();
    if (fresh.error) throw new Error(`Cannot determine current on-hand: ${fresh.error.message}`);
    const latest = fresh.data?.variants ?? [];
    if (latest.length === 0) throw new Error("Cannot determine current on-hand: no sellable variant.");
    if (!isVariant) {
      const n = Number(qty);
      if (!Number.isFinite(n) || n < 0) throw new Error("Enter a quantity.");
      const current = latest[0];
      if (!current?.determined) throw new Error("Cannot determine current on-hand.");
      await applyOne(current.id, n, current.onHand);
      return;
    }
    for (const v of latest) {
      const raw = rows[v.id];
      if (raw == null || raw.trim() === "") continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) continue;
      if (!v.determined) throw new Error("Cannot determine current on-hand.");
      await applyOne(v.id, n, v.onHand);
    }
  };

  const savePrices = async (asExceptions: boolean) => {
    const built = buildVariantPriceSaveLines(variants, priceInputs, productSellingPrice);
    if (built.ok === false) throw new Error(built.error);
    const promote = uniformOverridePromotionCandidate(built.lines, productSellingPrice);
    if (promote && !asExceptions) {
      await productApi.updateProductSellingPrice(props.orgId, props.productId, promote.amount);
      return;
    }
    await productApi.saveVariantSellingPrices(props.orgId, props.productId, built.lines);
  };

  const save = async (asExceptions = false) => {
    if (!locationId) {
      setError("Choose a location before adjusting stock.");
      return;
    }
    const doStock = stockInputsDirty(rows, qty, isVariant);
    const doPrice = priceInputsDirty(priceInputs, seededPrices);
    if (!doStock && !doPrice) {
      props.onClose();
      return;
    }
    setBusy(true);
    setError("");
    let stockError: string | null = null;
    let priceError: string | null = null;
    if (doStock) {
      try {
        await saveStock();
      } catch (e) {
        stockError = (e as Error).message;
      }
    }
    if (doPrice) {
      try {
        await savePrices(asExceptions);
      } catch (e) {
        priceError = (e as Error).message;
      }
    }
    const outcome = describeStockPriceSaveOutcome({
      stockAttempted: doStock,
      priceAttempted: doPrice,
      stockError,
      priceError,
    });
    if (!outcome.ok) {
      setError(outcome.message ?? "Save failed.");
      setBusy(false);
      return;
    }
    props.onSaved();
    props.onClose();
    setBusy(false);
  };

  const loadFailed = Boolean(loadError) || (props.open && !locationId);
  const locationName = locations?.find((l) => l.id === locationId)?.name ?? props.locationName;

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={`Stock · ${props.productName}`}
      size="drawer"
      footer={
        promote && !forceExceptions ? (
          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" onClick={() => setForceExceptions(true)} disabled={busy} className="px-4 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg">
              Save as variant exceptions
            </button>
            <button type="button" onClick={() => void save(false)} disabled={busy || loadFailed || isFetching} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg disabled:opacity-60">
              {busy ? "Saving..." : "Update product base"}
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => void save(forceExceptions)} disabled={busy || loadFailed || isFetching} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg disabled:opacity-60">
            {busy ? "Saving..." : "Save"}
          </button>
        )
      }
    >
      <div className="space-y-3">
        {error && <div className="px-3 py-2 bg-red-50 text-sm text-red-700 rounded-lg">{error}</div>}
        {loadError && <div className="px-3 py-2 bg-red-50 text-sm text-red-700 rounded-lg">Cannot determine current on-hand: {(loadError as Error).message}</div>}
        <label className="block text-xs font-medium text-slate-500">
          Location
          <select
            value={locationId ?? ""}
            onChange={(e) => {
              setLocationPicked(true);
              setLocationId(e.target.value || null);
            }}
            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800"
          >
            {(locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} · on hand {l.onHand}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-slate-500">
          On-hand is read for {locationName ?? "this location"} only, immediately before save.
          Blank price override inherits the product price{productSellingPrice != null ? ` (${money(productSellingPrice)})` : ""}.
          Stock and price save separately — changing one never mutates the other, and neither publishes to marketplaces.
          Uniform variant amounts do not change the product default unless you choose Update product base.
        </p>
        {promote && !forceExceptions && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Every variant is set to the same amount ({money(promote.amount)}). That does not change the product default unless you choose Update product base.
          </p>
        )}
        <div className="flex gap-2 flex-wrap">
          {(["set", "add", "sub"] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} className={`px-2 py-1 text-xs rounded-lg border ${mode === m ? "border-emerald-600 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
              {m === "set" ? "Set exact qty" : m === "add" ? "+ adjustment" : "− adjustment"}
            </button>
          ))}
        </div>
        {isVariant && (
          <div className="flex flex-wrap items-end gap-2">
            <button type="button" onClick={() => setPriceInputs((cur) => bulkClearOverrides(cur, targetIds()))} className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600">
              Use product price
            </button>
            <div className="flex items-center gap-1">
              <input
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                className="w-24 px-2 py-1 border border-slate-200 rounded text-xs"
                placeholder="Amount"
                aria-label="Bulk override amount"
              />
              <button type="button" onClick={() => setPriceInputs((cur) => bulkSetOverrideAmount(cur, targetIds(), bulkAmount))} className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600">
                Set selected
              </button>
            </div>
            <div className="flex items-center gap-1">
              <input
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                className="w-20 px-2 py-1 border border-slate-200 rounded text-xs"
                placeholder="Adj"
                aria-label="Price adjustment amount"
              />
              <button
                type="button"
                onClick={() => {
                  const result = bulkAdjustSelected(variants, priceInputs, targetIds(), Number(adjustAmount), productSellingPrice);
                  if (result.ok === false) setError(result.error);
                  else {
                    setError("");
                    setPriceInputs(result.inputs);
                  }
                }}
                className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600"
              >
                + adj
              </button>
              <button
                type="button"
                onClick={() => {
                  const result = bulkAdjustSelected(variants, priceInputs, targetIds(), -Number(adjustAmount), productSellingPrice);
                  if (result.ok === false) setError(result.error);
                  else {
                    setError("");
                    setPriceInputs(result.inputs);
                  }
                }}
                className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600"
              >
                − adj
              </button>
            </div>
          </div>
        )}
        {!isVariant ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              {simple ? `${simple.available} available` : "—"}
              {simple ? ` · ${simple.onHand} on hand` : ""}
            </p>
            {simple && (
              <p className="text-sm text-slate-600">
                Effective price {(() => {
                  const preview = previewEffectivePrice(priceInputs[simple.id] ?? "", productSellingPrice);
                  return preview.amount == null ? "—" : `${money(preview.amount)} · ${preview.kind === "override" ? "Override" : "Inherited"}`;
                })()}
              </p>
            )}
            <input value={qty} onChange={(e) => setQty(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Quantity" />
            {simple && (
              <input
                value={priceInputs[simple.id] ?? ""}
                onChange={(e) => setPriceInputs((r) => ({ ...r, [simple.id]: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder={productSellingPrice != null ? `Inherited ${productSellingPrice}` : "Price override"}
                aria-label={`Override ${simple.sku ?? simple.id}`}
              />
            )}
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[min(60vh,520px)]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          if (allSelected) setSelected({});
                          else {
                            const next: Record<string, boolean> = {};
                            for (const r of variants) next[r.id] = true;
                            setSelected(next);
                          }
                        }}
                        aria-label="Select all variants"
                      />
                    </th>
                    <th className="px-2 py-2 text-left">Variant</th>
                    <th className="px-2 py-2 text-left">SKU</th>
                    <th className="px-2 py-2 text-left">Available</th>
                    <th className="px-2 py-2 text-left">New qty</th>
                    <th className="px-2 py-2 text-left">Effective price</th>
                    <th className="px-2 py-2 text-left">Price override</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => {
                    const preview = previewEffectivePrice(priceInputs[v.id] ?? "", productSellingPrice);
                    return (
                      <tr key={v.id} className="border-t border-slate-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selected[v.id]}
                            onChange={() => setSelected((s) => ({ ...s, [v.id]: !s[v.id] }))}
                            aria-label={`Select ${v.sku ?? v.id}`}
                          />
                        </td>
                        <td className="px-2 py-2">{v.label}</td>
                        <td className="px-2 py-2 font-mono text-xs">{v.sku ?? "—"}</td>
                        <td className="px-2 py-2">
                          {v.available} available
                          <span className="block text-[11px] text-slate-400">{v.onHand} on hand</span>
                        </td>
                        <td className="px-2 py-2">
                          <input value={rows[v.id] ?? ""} onChange={(e) => setRows((r) => ({ ...r, [v.id]: e.target.value }))} className="w-20 px-2 py-1 border border-slate-200 rounded text-xs" aria-label={`New qty ${v.sku ?? v.id}`} />
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {preview.amount == null ? "—" : money(preview.amount)}
                          <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">
                            {preview.kind === "override" ? "Override" : "Inherited"}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            value={priceInputs[v.id] ?? ""}
                            onChange={(e) => setPriceInputs((r) => ({ ...r, [v.id]: e.target.value }))}
                            className="w-24 px-2 py-1 border border-slate-200 rounded text-xs"
                            placeholder="Inherit"
                            aria-label={`Override ${v.sku ?? v.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
